import { useContext, useEffect, useMemo, useState } from "react";

import { LineChart } from "@magnetic/charts";
import { Header } from "@magnetic/header";

import { CopyColumn } from "src/components/tables/columnConfigs/copyColumn";
import { GenericTypeColumn } from "src/components/tables/columnConfigs/genericTypeColumn";
import { NumberColumn } from "src/components/tables/columnConfigs/numberColumn";
import { StringColumn } from "src/components/tables/columnConfigs/stringColumn";
import { TimeColumn } from "src/components/tables/columnConfigs/timeColumn";
import TypedTable from "src/components/typedTable";
import { VFlex } from "src/components/vFlex";
import { DataViewerType, SyncCatType } from "src/dataTypes/dataViewerType";
import { FORMAT } from "src/dataTypes/dateFormats";
import { Message } from "src/gen/schema/syncfollow/syncfollow";
import useElementResizeObserver from "src/hooks/useElementResizeObserver";

import { StreamingCachePrintType } from "./dataflow/streamingCache";
import { Stream } from "./streaming/core/stream";
import { MonitorOverlay } from "./monitorOverlay";
import { TimeZoneContext } from "./timeZoneProvider";

type DataPoint = {
  id: number;
  x: number; // timestamp
  y: number;
  kind: string;
};

const COLOR_BLUE = { color: "#049fd9" };
const COLOR_RED = { color: "#cc2d37" };
const POLL_INTERVAL_MS = 250;
const MAX_POINTS = 600;

const StreamingMetricsChart = () => {
  const [containerRef, width] = useElementResizeObserver<HTMLDivElement>();
  const [openSeries, setOpenSeries] = useState<DataPoint[]>([]);
  const [pendingSeries, setPendingSeries] = useState<DataPoint[]>([]);
  const { getFormattedDate } = useContext(TimeZoneContext);

  // poll cache status every interval and update the series state data accordingly
  useEffect(() => {
    const interval = setInterval(() => {
      // non-blocking

      const now = Date.now();
      const openStreams = window.cnc.getStreams();
      const openStreamCount = openStreams.length;
      const consumers = openStreams.reduce(
        (count: number, s: Stream<Message, Message>) => {
          return count + s.followers();
        },
        0
      );

      setOpenSeries((open: DataPoint[]) => {
        // add new data point and truncate
        return open
          .concat([
            {
              id: now, // stable id to make the animations blend, this may work when meraki fixes their chart
              kind: "a",
              x: now,
              y: openStreamCount
            }
          ])
          .slice(-MAX_POINTS); // trucate series to n max points])
      });

      setPendingSeries((opening: DataPoint[]) => {
        // add new data point and truncate
        return opening
          .concat([
            {
              id: now, // stable id to make the animations blend, this may work when meraki fixes their chart
              kind: "b",
              x: now,
              y: consumers
            }
          ])
          .slice(-MAX_POINTS); // trucate series to n max points
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className="port-counter-chart">
      <LineChart
        animate={true}
        motionConfig="stiff"
        colors={["#049fd9", "#cc2d37"]}
        axes={{
          xAxis: {
            fontSize: 12,
            format: (d) => {
              return getFormattedDate(new Date(d), FORMAT.TIME_SHORT);
            },
            tickValues: 3
          },
          yAxis: {
            fontSize: 12,
            tickValues: 2
          }
        }}
        baseline={1}
        fill={false}
        data={
          [
            {
              data: openSeries,
              id: "open"
            },
            {
              data: pendingSeries,
              id: "pending"
            }
          ] as any /* eslint-disable-line @typescript-eslint/no-explicit-any */
          // need any because of chart implementation
        }
        fontSize={12}
        height={200}
        lineWidth={3}
        width={width}
        gridX={true}
        gridY={true}
        theme={{
          background: "var(--base-bg-weak-default)",
          fontSize: 12,
          margin: {
            bottom: 40,
            left: 80,
            right: 0,
            top: 20
          }
        }}
      />
    </div>
  );
};

const StreamingCacheEntriesTable = () => {
  const [data, setData] = useState<StreamingCachePrintType[]>([]);
  // poll cache status every interval and update the series state data accordingly

  useEffect(() => {
    const interval = setInterval(() => {
      // update cache content data for table view/export
      const content = window.cnc.showContent();

      // inject a link that has a link to the syncat tool with filters prepopulated via url parameters
      setData(
        content.map((d) => {
          let link = "/dev/syncfollow?schemaType=" + d.schemaType;

          const objectId = d.objectId || d.switchId || d.fabricId;
          link += "&objectId=" + objectId;
          link += "&url=" + encodeURIComponent(d.url);

          return {
            ...d,
            syncatLink: new SyncCatType(link),
            objectId: objectId,
            dataJsx: new DataViewerType({
              data: d.data,
              expandDepth: 0
            }),
            requestJsx: new DataViewerType({
              data: d.requestObject,
              expandDepth: 0
            })
          };
        })
      );
    }, POLL_INTERVAL_MS * 4);
    return () => clearInterval(interval);
  }, []);

  const columns = useMemo(() => {
    return {
      service: new StringColumn({
        label: "Service"
      }),
      schemaType: new StringColumn({
        label: "Schema Type"
      }),
      objectId: new StringColumn({
        label: "ObjectId"
      }),
      lastMessage: new TimeColumn({
        label: "Last Message"
      }),
      messages: new NumberColumn({
        label: "Messages"
      }),
      observers: new NumberColumn({
        label: "Observers"
      }),
      dataJsx: new GenericTypeColumn<DataViewerType>({
        label: "Merged Data"
      }),
      data: new CopyColumn({
        label: "Copy Data"
      }),
      syncatLink: new GenericTypeColumn<SyncCatType>({
        label: "Sync Monitor"
      }),
      requestJsx: new GenericTypeColumn<DataViewerType>({
        label: "Actual Request"
      })
    };
  }, []);

  return <TypedTable name="streamingCache" columns={columns} data={data} />;
};

// goal of this component is to make it easy to monitor current streams
type StreamingCacheMonitorProps = {
  readonly isOpen: boolean;
  readonly onToggle: () => void;
};

export const StreamingCacheMonitor = (
  props: StreamingCacheMonitorProps
): JSX.Element => {
  const { isOpen, onToggle } = props;

  // Add streams by type to component state.
  const [lastOpenCount, setLastOpenCount] = useState<number>(0);
  const [lastOpeningCount, setLastOpeningCount] = useState<number>(0);

  // poll cache status every interval and update the series state data accordingly
  useEffect(() => {
    const interval = setInterval(() => {
      const openStreams = window.cnc.getStreams();
      const openStreamCount = openStreams.length;
      const consumers = openStreams.reduce(
        (count: number, s: Stream<Message, Message>) => {
          return count + s.followers();
        },
        0
      );
      setLastOpeningCount(consumers);
      setLastOpenCount(openStreamCount);
    }, POLL_INTERVAL_MS * 2);
    return () => clearInterval(interval);
  }, []);

  const actions = (
    <VFlex gap="xxs">
      <span>
        <span>Open Streams: </span>
        <span style={COLOR_BLUE}>{lastOpenCount}</span>
      </span>
      <span>
        <span>Total Consumers: </span>
        <span style={COLOR_RED}>{lastOpeningCount}</span>
      </span>
    </VFlex>
  );

  // Render two buttons in the header, first indicating open connections (check), second pending unopen connections (bell)
  return (
    <>
      <Header.Button
        icon="tasks"
        label="Streaming Monitor"
        alert={lastOpenCount}
        onClick={onToggle}
      />
      <MonitorOverlay
        title="Streaming Monitor"
        isOpen={isOpen}
        onClose={onToggle}
        actions={actions}
      >
        <>
          <StreamingMetricsChart />
          <StreamingCacheEntriesTable />
        </>
      </MonitorOverlay>
    </>
  );
};
